/**
 * Tabela com as pessoas que têm conta e o resumo do acesso de cada uma.
 *
 * Papel: desenha uma linha por `Usuario` da página atual, com nome e e-mail, o
 * resumo das permissões, o último acesso e o botão que abre a grade.
 *
 * Como usar: a tabela recebe a página já filtrada e ordenada pela API e não
 * reordena nem filtra nada por conta própria. Ordenar no navegador reordenaria
 * só os cinquenta registros da página, e a pessoa concluiria que alguém da
 * página seguinte não existe; a busca e a paginação ficam com quem monta a
 * tabela, que as passa à consulta.
 *
 * As colunas são declaradas fora do componente, e o manipulador de edição chega
 * às células por `table.options.meta`, e não por fechamento. Declarar as colunas
 * dentro do componente para alcançar o manipulador recriaria o array a cada
 * render e faria a tabela remontar todas as linhas.
 */
import { ROTULOS_MODULO, type Permissao, type Usuario } from '@concierge/shared';
import { createColumnHelper, tableFeatures, useTable } from '@tanstack/react-table';

import { formatarInstante } from '@/shared/lib/formato';
import { Button } from '@/shared/ui/button';
import { Skeleton } from '@/shared/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/ui/table';

/** O que as células recebem de quem monta a tabela. */
interface MetaDaTabela {
  onEditar: (usuario: Usuario) => void;
}

const recursos = tableFeatures({ tableMeta: {} as MetaDaTabela });
const coluna = createColumnHelper<typeof recursos, Usuario>();

/** Quantas linhas de esqueleto desenhar enquanto a primeira página carrega. */
const LINHAS_DE_ESPERA = 5;

/** Página vazia estável, para a tabela não recalcular linhas a cada render. */
const NENHUM_USUARIO: Usuario[] = [];

/**
 * Resume as permissões em uma frase curta para a coluna de acesso.
 *
 * Nomeia os módulos quando são até dois, porque é o caso comum — alguém que
 * cuida só do Mailing — e o nome diz mais do que a contagem; acima disso a frase
 * listaria meia tela e empurraria as outras colunas.
 *
 * @param permissoes - O conjunto gravado para a pessoa.
 * @returns `Sem acesso`, os nomes dos módulos, ou a contagem com quantos editam.
 */
function resumirPermissoes(permissoes: readonly Permissao[]): string {
  if (permissoes.length === 0) return 'Sem acesso';

  if (permissoes.length <= 2) {
    return permissoes
      .map((p) => `${ROTULOS_MODULO[p.modulo]} (${p.nivel === 'editar' ? 'edita' : 'vê'})`)
      .join(', ');
  }

  const editando = permissoes.filter((p) => p.nivel === 'editar').length;
  const sufixo = editando === 0 ? '' : `, ${editando} com edição`;
  return `${permissoes.length} módulos${sufixo}`;
}

const COLUNAS = coluna.columns([
  coluna.accessor('nome', {
    header: 'Pessoa',
    cell: ({ row }) => {
      const { nome, email, auth0Sub } = row.original;
      return (
        <div className="min-w-0">
          <p className="truncate text-foreground">{nome ?? email ?? 'Sem nome no Auth0'}</p>
          <p className="truncate text-xs text-muted-foreground">
            {nome !== null && email !== null ? email : auth0Sub}
          </p>
        </div>
      );
    },
  }),
  coluna.accessor('permissoes', {
    header: 'Acesso',
    cell: ({ getValue }) => (
      <span className="text-sm text-muted-foreground">{resumirPermissoes(getValue())}</span>
    ),
  }),
  coluna.accessor('ultimoAcessoEm', {
    header: 'Último acesso',
    cell: ({ getValue }) => (
      <span className="tabular text-sm">{formatarInstante(getValue())}</span>
    ),
  }),
  coluna.display({
    id: 'acoes',
    header: () => <span className="sr-only">Ações</span>,
    cell: ({ row, table }) => (
      <div className="text-right">
        <Button
          variant="outline"
          size="sm"
          aria-label={`Editar permissões de ${row.original.nome ?? row.original.email ?? row.original.auth0Sub}`}
          onClick={() => table.options.meta?.onEditar(row.original)}
        >
          Permissões
        </Button>
      </div>
    ),
  }),
]);

interface TabelaUsuariosProps {
  usuarios: Usuario[] | undefined;
  carregando: boolean;
  mensagemVazia: string;
  onEditar: (usuario: Usuario) => void;
}

/**
 * Tabela de usuários com os estados de espera e de lista vazia.
 *
 * O esqueleto só aparece enquanto não há página nenhuma; ao trocar de página, a
 * anterior continua na tela até a nova chegar, porque o `useUsuarios` guarda o
 * dado anterior, e a tabela não salta de altura.
 *
 * @param usuarios - A página atual, ou `undefined` antes da primeira resposta.
 * @param carregando - Verdadeiro enquanto a primeira página não chegou.
 * @param mensagemVazia - Texto da linha única quando a página vem sem ninguém;
 *   quem monta a tabela sabe se há busca aplicada e escolhe a frase.
 * @param onEditar - Chamado com a pessoa da linha ao clicar em `Permissões`.
 * @returns A tabela, com esqueleto, linhas ou a mensagem de vazio.
 */
export function TabelaUsuarios({
  usuarios,
  carregando,
  mensagemVazia,
  onEditar,
}: TabelaUsuariosProps) {
  const tabela = useTable({
    features: recursos,
    columns: COLUNAS,
    data: usuarios ?? NENHUM_USUARIO,
    getRowId: (usuario) => usuario.id,
    meta: { onEditar },
  });

  const linhas = tabela.getRowModel().rows;

  return (
    <div className="overflow-x-auto rounded-md border border-border bg-card">
      <Table>
        <TableHeader>
          {tabela.getHeaderGroups().map((grupo) => (
            <TableRow key={grupo.id}>
              {grupo.headers.map((cabecalho) => (
                <TableHead key={cabecalho.id}>
                  {cabecalho.isPlaceholder ? null : <tabela.FlexRender header={cabecalho} />}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {carregando ? (
            Array.from({ length: LINHAS_DE_ESPERA }, (_, indice) => (
              <TableRow key={indice}>
                <TableCell colSpan={COLUNAS.length}>
                  <Skeleton className="h-9 w-full" />
                </TableCell>
              </TableRow>
            ))
          ) : linhas.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={COLUNAS.length}
                className="py-10 text-center text-sm text-muted-foreground"
              >
                {mensagemVazia}
              </TableCell>
            </TableRow>
          ) : (
            linhas.map((linha) => (
              <TableRow key={linha.id}>
                {linha.getAllCells().map((celula) => (
                  <TableCell key={celula.id} className="max-w-72">
                    <tabela.FlexRender cell={celula} />
                  </TableCell>
                ))}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}
